import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getWorkspaceItems,
  updateWorkspaceItem,
  approveWorkspaceItem,
  rejectWorkspaceItem,
  getDocumentDownloadUrl,
} from '../../services/api';

const DIVISIONS = ['Alpha Division', 'Beta Division', 'Gamma Division', 'Delta Division'];
const CONTRACT_TYPES = ['CPFF', 'T&M', 'FFP', 'CPIF', 'IDIQ'];

const STATUS_DISPLAY = {
  PENDING_EXTRACTION: { label: 'Pending Extraction', className: 'pending' },
  PENDING_COMPLETION: { label: 'Pending Completion', className: 'review' },
  PENDING_REVIEW: { label: 'Pending Review', className: 'review' },
  APPROVED: { label: 'Approved', className: 'active' },
  REJECTED: { label: 'Rejected', className: 'critical' },
};

function formatCurrency(val) {
  if (val == null || val === '') return '';
  const num = Number(val);
  if (isNaN(num)) return String(val);
  return num.toLocaleString('en-US');
}

/* ── Extracted subcomponents to reduce cognitive complexity ── */

function ExtractionProgressText({ progress }) {
  if (progress < 30) return 'Extracting text from document...';
  if (progress < 60) return 'Analyzing contract data with AI...';
  if (progress < 90) return 'Matching against existing contracts...';
  return 'Finalizing extraction...';
}

function ExtractionView({ item, statusInfo, extractionProgress, onBack, onViewDocument }) {
  return (
    <div className="workspace-detail">
      <BackLink onClick={onBack} />

      <div className="ws-detail-header">
        <div className="ws-detail-header-left">
          <h3 className="ws-detail-title">{item.documentName}</h3>
          <div className="ws-detail-meta">
            <span className={`badge-status ${statusInfo.className}`}>
              <span className="badge-dot"></span>
              {statusInfo.label}
            </span>
            <span className="ws-detail-meta-item">Uploaded by {item.uploadedBy}</span>
            <span className="ws-detail-meta-item">{item.documentType}</span>
          </div>
        </div>
      </div>

      <div className="ws-extraction-card">
        <div className="ws-extraction-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="ws-spin">
            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          </svg>
        </div>
        <h3 className="ws-extraction-title">Processing Document</h3>
        <p className="ws-extraction-desc">
          AI is extracting contract data from your document. This may take up to 2–3 minutes for scanned PDFs that require OCR processing.
        </p>
        <div className="ws-extraction-progress-bar">
          <div className="ws-extraction-progress-fill" style={{ width: `${extractionProgress}%` }} />
        </div>
        <p className="ws-extraction-status">
          <ExtractionProgressText progress={extractionProgress} />
        </p>

        {item.documentKey && (
          <button className="btn btn-outline" style={{ marginTop: '16px' }} onClick={onViewDocument}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            View Source Document
          </button>
        )}
      </div>
    </div>
  );
}

function BackLink({ onClick }) {
  return (
    <button className="form-back-link" onClick={onClick}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
      </svg>
      Back to Workspace
    </button>
  );
}

function StatusBanners({ item, onNavigate }) {
  return (
    <>
      {item.status === 'REJECTED' && item.reviewNotes && (
        <div className="ws-rejection-banner">
          <div className="ws-rejection-label">Rejection Notes</div>
          <div className="ws-rejection-text">{item.reviewNotes}</div>
          <div className="ws-rejection-meta">
            Rejected by {item.reviewedBy} on {new Date(item.reviewedAt).toLocaleDateString()}
          </div>
        </div>
      )}

      {item.status === 'APPROVED' && item.contractRecordId && (
        <div className="ws-approved-banner">
          <div className="ws-approved-label">Contract Created</div>
          <div className="ws-approved-text">Record ID: {item.contractRecordId}</div>
          <button className="btn btn-sm btn-outline" onClick={() => onNavigate('contracts')}>
            View in Contracts
          </button>
        </div>
      )}
    </>
  );
}

function ProjectMatchBadge({ isNewProject }) {
  if (isNewProject === true) {
    return (
      <span className="badge-status accepted">
        <span className="badge-dot"></span>
        New Project
      </span>
    );
  }
  if (isNewProject === false) {
    return (
      <span className="badge-status completed">
        <span className="badge-dot"></span>
        Matched
      </span>
    );
  }
  return null;
}

function ReviewDecisionSection({ showRejectNotes, setShowRejectNotes, reviewNotes, setReviewNotes, submitting, onApprove, onReject }) {
  return (
    <div className="form-section" style={{ marginTop: '24px' }}>
      <div className="form-section-title">Review Decision</div>

      <div className="form-group" style={{ marginBottom: '16px' }}>
        <label className="form-label">
          {showRejectNotes ? <>Review Notes <span className="required">*</span></> : 'Review Notes (optional)'}
        </label>
        <textarea
          className="form-input ws-textarea"
          value={reviewNotes}
          onChange={(e) => setReviewNotes(e.target.value)}
          placeholder={showRejectNotes ? 'Reason for rejection...' : 'Optional notes...'}
          rows={showRejectNotes ? 3 : 2}
        />
      </div>

      <div className="form-actions">
        {showRejectNotes ? (
          <>
            <button className="btn btn-outline" onClick={() => setShowRejectNotes(false)} disabled={submitting}>
              Cancel
            </button>
            <button
              className="btn btn-sm"
              style={{ background: 'var(--status-critical)', color: '#ffffff', borderColor: 'var(--status-critical)' }}
              onClick={onReject}
              disabled={submitting}
            >
              {submitting ? 'Rejecting...' : 'Confirm Reject'}
            </button>
          </>
        ) : (
          <>
            <button
              className="btn btn-outline"
              onClick={() => setShowRejectNotes(true)}
              disabled={submitting}
              style={{ borderColor: 'var(--status-critical)', color: 'var(--status-critical)' }}
            >
              Reject
            </button>
            <button className="btn btn-primary" onClick={onApprove} disabled={submitting}>
              {submitting ? 'Approving...' : 'Approve & Create Contract'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Main Component ── */

function WorkspaceDetail({ workspaceId, onNavigate, isAdmin }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [showRejectNotes, setShowRejectNotes] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(0);

  const fetchItem = useCallback(async () => {
    try {
      const items = await getWorkspaceItems();
      const found = items.find((i) => i.workspaceId === workspaceId);
      if (found) {
        setItem(found);
        const extracted = found.extractedData || {};
        const userData = found.userCompletedData || {};
        setForm({ ...extracted, ...userData });
        return found;
      } else {
        setError('Workspace item not found');
        return null;
      }
    } catch (err) {
      setError(err.message || 'Failed to load workspace item');
      return null;
    }
  }, [workspaceId]);

  // Initial fetch
  useEffect(() => {
    let cancelled = false;
    async function init() {
      setLoading(true);
      await fetchItem();
      if (!cancelled) setLoading(false);
    }
    init();
    return () => { cancelled = true; };
  }, [fetchItem]);

  // Poll when status is PENDING_EXTRACTION
  useEffect(() => {
    if (!item || item.status !== 'PENDING_EXTRACTION') return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 90; // up to ~3 minutes

    const interval = setInterval(async () => {
      if (cancelled) return;
      attempts++;
      setExtractionProgress(Math.min(95, Math.round((attempts / maxAttempts) * 100)));

      try {
        const items = await getWorkspaceItems();
        const found = items.find((i) => i.workspaceId === workspaceId);
        if (found && found.status !== 'PENDING_EXTRACTION') {
          if (!cancelled) {
            setItem(found);
            const extracted = found.extractedData || {};
            const userData = found.userCompletedData || {};
            setForm({ ...extracted, ...userData });
            setExtractionProgress(100);
          }
        }
      } catch { }

      if (attempts >= maxAttempts && !cancelled) {
        clearInterval(interval);
      }
    }, 2000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.status, workspaceId]);

  const isEditable = item?.status === 'PENDING_COMPLETION' || item?.status === 'REJECTED';
  const isReviewable = item?.status === 'PENDING_REVIEW';
  const isReadOnly = !isEditable;
  const isExtracting = item?.status === 'PENDING_EXTRACTION';

  // Fields that were null in extraction (need user input)
  const nullFields = useMemo(() => {
    if (!item?.extractedData) return new Set();
    const fields = new Set();
    Object.entries(item.extractedData).forEach(([key, val]) => {
      if (val === null || val === '' || val === undefined) {
        fields.add(key);
      }
    });
    return fields;
  }, [item]);

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmitCompletion() {
    setApiError('');
    setSubmitting(true);
    try {
      await updateWorkspaceItem(workspaceId, form);
      onNavigate('workspace');
    } catch (err) {
      setApiError(err.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleApprove() {
    setApiError('');
    setSubmitting(true);
    try {
      await approveWorkspaceItem(workspaceId, reviewNotes);
      const contractNumber = form.contractNumber || '';
      onNavigate('contracts', { searchQuery: contractNumber });
    } catch (err) {
      setApiError(err.message || 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReject() {
    if (!reviewNotes.trim()) {
      setApiError('Review notes are required when rejecting');
      return;
    }
    setApiError('');
    setSubmitting(true);
    try {
      await rejectWorkspaceItem(workspaceId, reviewNotes);
      onNavigate('workspace');
    } catch (err) {
      setApiError(err.message || 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleViewDocument() {
    if (!item?.documentKey) return;
    try {
      const result = await getDocumentDownloadUrl(item.documentKey);
      window.open(result.downloadUrl, '_blank');
    } catch (err) {
      setApiError(err.message || 'Failed to get document URL');
    }
  }

  function fieldClass(field) {
    const classes = ['form-input'];
    if (isReadOnly) classes.push('readonly');
    if (nullFields.has(field) && isEditable) classes.push('needs-input');
    return classes.join(' ');
  }

  function selectClass(field) {
    const classes = ['form-select'];
    if (isReadOnly) classes.push('readonly');
    if (nullFields.has(field) && isEditable) classes.push('needs-input');
    return classes.join(' ');
  }

  if (loading) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '60px',
        color: 'var(--bcore-text-dim)',
        fontSize: '13px',
        textTransform: 'uppercase',
        letterSpacing: '1px',
      }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-page">
        <div className="form-api-error">{error}</div>
        <button className="btn btn-outline" onClick={() => onNavigate('workspace')}>
          Back to Workspace
        </button>
      </div>
    );
  }

  if (!item) return null;

  const statusInfo = STATUS_DISPLAY[item.status] || { label: item.status, className: '' };

  // Extraction in progress — show processing overlay
  if (isExtracting) {
    return (
      <ExtractionView
        item={item}
        statusInfo={statusInfo}
        extractionProgress={extractionProgress}
        onBack={() => onNavigate('workspace')}
        onViewDocument={handleViewDocument}
      />
    );
  }

  return (
    <div className="workspace-detail">
      <BackLink onClick={() => onNavigate('workspace')} />

      {/* Header Bar */}
      <div className="ws-detail-header">
        <div className="ws-detail-header-left">
          <h3 className="ws-detail-title">{item.documentName}</h3>
          <div className="ws-detail-meta">
            <span className={`badge-status ${statusInfo.className}`}>
              <span className="badge-dot"></span>
              {statusInfo.label}
            </span>
            <span className="ws-detail-meta-item">Uploaded by {item.uploadedBy}</span>
            <span className="ws-detail-meta-item">{item.documentType}</span>
            <ProjectMatchBadge isNewProject={item.isNewProject} />
          </div>
        </div>
      </div>

      <StatusBanners item={item} onNavigate={onNavigate} />

      {/* PENDING_COMPLETION hint */}
      {isEditable && (
        <div className="ws-hint-banner">
          Fields highlighted with a yellow border need your input. Fill in missing data and submit for review.
        </div>
      )}

      <div className="ws-detail-body">
        {/* Left: Contract Form */}
        <div className="ws-detail-form">
          <div className="form-card">
            {/* Contract Identification */}
            <div className="form-section">
              <div className="form-section-title">Contract Identification</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Division
                    {nullFields.has('division') && isEditable && <span className="needs-input-tag">Needs Input</span>}
                  </label>
                  {isReadOnly ? (
                    <input type="text" className="form-input readonly" value={form.division || ''} readOnly />
                  ) : (
                    <select
                      className={selectClass('division')}
                      value={form.division || ''}
                      onChange={(e) => handleChange('division', e.target.value)}
                    >
                      <option value="">Select Division</option>
                      {DIVISIONS.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Program
                    {nullFields.has('program') && isEditable && <span className="needs-input-tag">Needs Input</span>}
                  </label>
                  <input
                    type="text"
                    className={fieldClass('program')}
                    value={form.program || ''}
                    onChange={(e) => handleChange('program', e.target.value)}
                    readOnly={isReadOnly}
                    placeholder="Program name"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Project Name
                    {nullFields.has('projectName') && isEditable && <span className="needs-input-tag">Needs Input</span>}
                  </label>
                  <input
                    type="text"
                    className={fieldClass('projectName')}
                    value={form.projectName || ''}
                    onChange={(e) => handleChange('projectName', e.target.value)}
                    readOnly={isReadOnly}
                    placeholder="Project or task name"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Contract #</label>
                  <input
                    type="text"
                    className={fieldClass('contractNumber')}
                    value={form.contractNumber || ''}
                    onChange={(e) => handleChange('contractNumber', e.target.value)}
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Contract Type
                    {nullFields.has('contractType') && isEditable && <span className="needs-input-tag">Needs Input</span>}
                  </label>
                  {isReadOnly ? (
                    <input type="text" className="form-input readonly" value={form.contractType || ''} readOnly />
                  ) : (
                    <select
                      className={selectClass('contractType')}
                      value={form.contractType || ''}
                      onChange={(e) => handleChange('contractType', e.target.value)}
                    >
                      <option value="">Select Type</option>
                      {CONTRACT_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Direction</label>
                  <input
                    type="text"
                    className={fieldClass('direction')}
                    value={form.direction || ''}
                    onChange={(e) => handleChange('direction', e.target.value)}
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Company</label>
                  <input
                    type="text"
                    className={fieldClass('company')}
                    value={form.company || ''}
                    onChange={(e) => handleChange('company', e.target.value)}
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Customer</label>
                  <input
                    type="text"
                    className={fieldClass('customer')}
                    value={form.customer || ''}
                    onChange={(e) => handleChange('customer', e.target.value)}
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">End Customer</label>
                  <input
                    type="text"
                    className={fieldClass('endCustomer')}
                    value={form.endCustomer || ''}
                    onChange={(e) => handleChange('endCustomer', e.target.value)}
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">NAICS</label>
                  <input
                    type="text"
                    className={fieldClass('naics')}
                    value={form.naics || ''}
                    onChange={(e) => handleChange('naics', e.target.value)}
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">PO Number</label>
                  <input
                    type="text"
                    className={fieldClass('purchaseOrderNumber')}
                    value={form.purchaseOrderNumber || ''}
                    onChange={(e) => handleChange('purchaseOrderNumber', e.target.value)}
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Exclusivity Clause</label>
                  <input
                    type="text"
                    className="form-input readonly"
                    value={form.exclusivityClause != null ? (form.exclusivityClause ? 'Yes' : 'No') : '--'}
                    readOnly
                  />
                </div>
              </div>
            </div>

            {/* Period Details */}
            <div className="form-section">
              <div className="form-section-title">Period of Performance</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Start Date
                    {nullFields.has('popStart') && isEditable && <span className="needs-input-tag">Needs Input</span>}
                  </label>
                  <input
                    type="date"
                    className={fieldClass('popStart')}
                    value={form.popStart || ''}
                    onChange={(e) => handleChange('popStart', e.target.value)}
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    End Date
                    {nullFields.has('popEnd') && isEditable && <span className="needs-input-tag">Needs Input</span>}
                  </label>
                  <input
                    type="date"
                    className={fieldClass('popEnd')}
                    value={form.popEnd || ''}
                    onChange={(e) => handleChange('popEnd', e.target.value)}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>
            </div>

            {/* Financial Data */}
            <div className="form-section">
              <div className="form-section-title">Financial Data</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Contract Value ($)
                    {nullFields.has('contractValue') && isEditable && <span className="needs-input-tag">Needs Input</span>}
                  </label>
                  <input
                    type="text"
                    className={fieldClass('contractValue')}
                    value={isReadOnly ? formatCurrency(form.contractValue) : (form.contractValue || '')}
                    onChange={(e) => handleChange('contractValue', e.target.value)}
                    readOnly={isReadOnly}
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Ceiling ($)
                    {nullFields.has('ceiling') && isEditable && <span className="needs-input-tag">Needs Input</span>}
                  </label>
                  <input
                    type="text"
                    className={fieldClass('ceiling')}
                    value={isReadOnly ? formatCurrency(form.ceiling) : (form.ceiling || '')}
                    onChange={(e) => handleChange('ceiling', e.target.value)}
                    readOnly={isReadOnly}
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Funded ($)
                    {nullFields.has('funded') && isEditable && <span className="needs-input-tag">Needs Input</span>}
                  </label>
                  <input
                    type="text"
                    className={fieldClass('funded')}
                    value={isReadOnly ? formatCurrency(form.funded) : (form.funded || '')}
                    onChange={(e) => handleChange('funded', e.target.value)}
                    readOnly={isReadOnly}
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Funding Per PoP ($)
                    {nullFields.has('fundingPerPop') && isEditable && <span className="needs-input-tag">Needs Input</span>}
                  </label>
                  <input
                    type="text"
                    className={fieldClass('fundingPerPop')}
                    value={isReadOnly ? formatCurrency(form.fundingPerPop) : (form.fundingPerPop || '')}
                    onChange={(e) => handleChange('fundingPerPop', e.target.value)}
                    readOnly={isReadOnly}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            {/* Labor Categories */}
            {(form.laborCategories && form.laborCategories.length > 0) && (
              <div className="form-section">
                <div className="form-section-title">Labor Categories</div>
                <div className="ws-labor-table">
                  <div className="ws-labor-header">
                    <span>Category</span>
                    <span>Rate</span>
                  </div>
                  {form.laborCategories.map((lc, idx) => (
                    <div key={idx} className="ws-labor-row">
                      <span>{lc.category || '--'}</span>
                      <span>{lc.rate != null ? `$${formatCurrency(lc.rate)}` : '--'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Key Personnel */}
            {(form.keyPersonnel && form.keyPersonnel.length > 0) && (
              <div className="form-section">
                <div className="form-section-title">Key Personnel</div>
                <div className="ws-personnel-list">
                  {form.keyPersonnel.map((name, idx) => (
                    <span key={idx} className="tag">{name}</span>
                  ))}
                </div>
              </div>
            )}

            {/* API Error */}
            {apiError && (
              <div className="form-api-error">{apiError}</div>
            )}

            {/* Actions */}
            {isEditable && (
              <div className="form-actions">
                <button className="btn btn-outline" onClick={() => onNavigate('workspace')} disabled={submitting}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSubmitCompletion} disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit for Review'}
                </button>
              </div>
            )}

            {isReviewable && (
              <ReviewDecisionSection
                showRejectNotes={showRejectNotes}
                setShowRejectNotes={setShowRejectNotes}
                reviewNotes={reviewNotes}
                setReviewNotes={setReviewNotes}
                submitting={submitting}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            )}
          </div>
        </div>

        {/* Right: Document Preview */}
        <div className="ws-detail-document">
          <div className="ws-document-panel">
            <div className="ws-document-panel-header">
              <span>Source Document</span>
              {item.documentKey && (
                <button className="btn btn-sm btn-outline" onClick={handleViewDocument}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px' }}>
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                  View
                </button>
              )}
            </div>
            <div className="ws-document-panel-body">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.4, marginBottom: '12px' }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <div className="ws-document-name">{item.documentName}</div>
              <div className="ws-document-meta">
                {item.documentType} &bull; Uploaded {new Date(item.uploadedAt).toLocaleDateString()}
              </div>
              <button className="btn btn-outline" style={{ marginTop: '12px', fontSize: '12px' }} onClick={handleViewDocument}>
                Open Document in New Tab
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkspaceDetail;
